<template>
	<div class="relative flex-grow">
		<EquipmentSharedDeviceToolTestPanel
			:variant-groups="variantGroups"
			reset-label="Reset"
			@set-variant="setVariant"
			@reset="resetVariant"
		/>

		<EquipmentSharedDeviceToolCard
			:progress="progress"
			:is-loading="isLoading"
			:is-device-supported="isDeviceSupported"
			:has-serial-number="!!serialNumber"
			:requires-serial-number="requiresSerialNumber"
			:status-text="statusText"
			:show-result="shouldShowResult"
			:error-keys="{
				notSupported: 'device-tools.reboot.not-supported',
				missingSerial: 'device-tools.reboot.missing-serial',
				failed: 'device-tools.reboot.failed'
			}"
			i18n-prefix="device-tools.reboot"
			:cooldown-seconds="cooldownSeconds"
			:status-label="statusLabel"
			:severity="severity"
			:button-config="{
				labelKey: 'device-tools.reboot.button-label',
				icon: ArrowPathIcon,
				hideWhenUnsupported: true
			}"
			show-unsupported-message
			unsupported-message-key="device-tools.reboot.unsupported"
			@button-click="handleRebootClick"
		>
		<!-- Title -->
		<template #title>
			{{ t('device-tools.reboot.title') }}
		</template>

		<!-- Result: Custom loading text and result content -->
		<template #result-line1-loading>
			{{ t('device-tools.reboot.in-progress.sending') }}
		</template>

		<!-- Only override default content when we have actual result -->
		<template v-if="shouldShowResult" #result-line1>
			{{ line1Content }}
		</template>

		<template v-if="shouldShowResult" #result-line2>
			{{ line2Content }}
		</template>

		<!-- Line 3 uses deviceInfo prop passed to parent -->
	</EquipmentSharedDeviceToolCard>
</div>

<!-- Confirmation Modal -->
	<ModalConfirmSave
		:id="modalId"
		:title="t('device-tools.reboot.modal.title')"
		:description="t('device-tools.reboot.modal.message')"
		:confirm-text="t('device-tools.reboot.modal.confirm')"
		@on-confirm="confirmReboot"
		@on-cancel="closeModal"
		@on-close="closeModal"
	/>
</template>

<script setup lang="ts">
import { useDeviceTools, useDeviceToolState } from '@/composables/equipment'
import { DEVICE_REBOOT_MOCKS, getDeviceRebootMockVariantGroups } from '@/composables/equipment/useDeviceToolTesting'
import { useModal } from '@/composables/useModal'
import { NMS_TYPES } from '@/utils/equipment/nmsDetection'
import { getRebootDescription, getRebootLabel, getRebootMessage } from '@/utils/equipment/rebootHelpers'
import { normalizeRebootResult } from '@/utils/equipment/rebootNormalization'
import { ArrowPathIcon } from '@heroicons/vue/24/outline'
import { useI18n } from 'vue-i18n'

const CONFIRM_DEVICE_REBOOT_MODAL_ID = 'device-reboot-confirm'

const { t } = useI18n()

const props = defineProps({
	macAddress: {
		type: String,
		required: true
	},
	serialNumber: {
		type: [String, null] as PropType<string | null>,
		default: null
	},
	deviceNms: {
		type: String,
		default: ''
	},
	config: {
		type: Object as PropType<{ apiDomain: 'internal' | 'customer' }>,
		required: true,
		default: () => ({ apiDomain: 'customer' })
	}
})

const { macAddress, serialNumber, deviceNms, config } = toRefs(props)

// Testing support - simplified mock injection pattern
const variantGroups = getDeviceRebootMockVariantGroups()

const { rebootDevice, deviceReboots, getDeviceKey, resetDeviceReboot, injectMockDeviceReboot } = useDeviceTools(config.value)
const { openModal, closeModal } = useModal()

const setVariant = (mockId: string) => {
	const mock = DEVICE_REBOOT_MOCKS.find(m => m.id === mockId)
	if (mock) {
		injectMockDeviceReboot(macAddress.value, mock.data)
	}
}

const resetVariant = () => {
	resetDeviceReboot(macAddress.value)
}

const modalId = ref(`${CONFIRM_DEVICE_REBOOT_MODAL_ID}_${macAddress.value || 'no-mac'}_${serialNumber.value || 'no-serial'}_${deviceNms.value || 'no-nms'}`)

const deviceKey = computed(() => getDeviceKey(macAddress.value))

// Reset state on component mount to ensure fresh state on navigation
onMounted(() => {
	resetDeviceReboot(macAddress.value)
})

const state = computed(() => deviceReboots[deviceKey.value])

// Use new shared composable for state management and UI flags
const {
	isLoading,
	progress,
	cooldownSeconds,
	result: rebootResult,
	severity,
	showResult,
	isDeviceSupported,
	statusText
} = useDeviceToolState({
	state,
	deviceNms,
	resultGetter: (state) => normalizeRebootResult(state),
	severityGetter: (result) => result?.severity,
	i18nKeys: {
		prefix: 'device-tools.reboot',
		ready: 'device-tools.reboot.ready',
		inProgress: 'device-tools.reboot.rebooting',
		finishing: 'device-tools.reboot.finishing'
	}
})

// Reboot-specific computed properties
const shouldShowResult = computed(() => showResult.value && !!rebootResult.value)

// Only Calix devices require serial number (UISP and unknown devices use MAC only)
const requiresSerialNumber = computed(() => deviceNms.value === NMS_TYPES.CALIX)

// Use utility functions for text content (result object pattern)
const statusLabel = computed(() => getRebootLabel(rebootResult.value, t))
const line1Content = computed(() => getRebootDescription(rebootResult.value, t))
const line2Content = computed(() => getRebootMessage(rebootResult.value, t))

const handleRebootClick = () => {
	if (isLoading.value) {
		return
	}
	openModal(modalId.value)
}

const confirmReboot = async () => {
	await rebootDevice(
		macAddress.value,
		serialNumber.value || undefined,
		deviceNms.value
	)
}
</script>
